import React from 'react';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { formatCurrency } from '../../../../CommonUtils/formatNumber';
import { AssetsTable } from '../../components/AssetsTable/AssetsTable';
import { TableActions } from '../../components/TableActions/TableActions';
import {
  columnHeaders,
  PensionDTO,
  PensionPlanTypeEnum,
  PensionsOptionsDTO,
} from './Pensions.meta';
import { AssetToUpsert } from '../../components/UpsertAssetModal/UpsertAssetModal.meta';
import { AssetToDelete } from '../../components/DeleteAssetModal/DeleteAssetModal.meta';
import { ASSET_NAME_MAP, OtherAssetType } from '../../OtherAssets.meta';
import { WorkflowComponentProps } from '../../../../Workflow/Workflow.meta';
import { WorkflowIdEnum } from '../../../../Workflow/api/useWorkflowDataQuery';
import { Icon } from '../../../../CommonComponents/OPL/Icon/Icon';
import { ClickableTableRow } from '../../../../CommonComponents/OPL/HtmlTable/HtmlTable';

interface Props {
  data: PensionDTO[];
  options: PensionsOptionsDTO;
  setAssetToUpsert: (assetToUpsert: AssetToUpsert | null) => void;
  setAssetToDelete: (assetToDelete: AssetToDelete | null) => void;
  workflowProps?: WorkflowComponentProps;
  onClone(asset: PensionDTO): void;
}

export const Pensions = ({
  data,
  options,
  setAssetToUpsert,
  setAssetToDelete,
  workflowProps,
  onClone,
}: Props) => {
  const isEstatePlanningWorkflow =
    workflowProps && workflowProps.workflowId === WorkflowIdEnum.EstatePlanning;

  return (
    <AssetsTable
      title="Pensions"
      columnHeaders={
        isEstatePlanningWorkflow
          ? [...columnHeaders, { text: 'In Gr. Est.', align: 'center' }]
          : columnHeaders
      }
      isEmpty={data.length === 0}
      workflowProps={workflowProps}
    >
      {data.map(asset => (
        <ClickableTableRow
          variant="clickable"
          key={asset.Id}
          onClick={() => {
            setAssetToUpsert(asset);
          }}
        >
          <td>
            <Text fontWeight={500} textAlign="left" width="100%">
              {asset.Recipient}
            </Text>
          </td>

          <td>
            <Text fontWeight={500} textAlign="left">
              {options.planTypeOptions.find(({ value }) => value === asset.PensionPlanType)?.label}
            </Text>
          </td>

          <td>
            <Text variant="tabular" fontWeight={500} textAlign="right">
              {asset.PensionPlanType !== PensionPlanTypeEnum.LumpSum &&
              asset.MonthlyPayment !== null
                ? formatCurrency(asset.MonthlyPayment)
                : '-'}
            </Text>
          </td>

          <td>
            <Text fontWeight={500} textAlign="left">
              {asset.DistributionAge ? asset.DistributionAge : 'At Retirement'}
            </Text>
          </td>

          {isEstatePlanningWorkflow && (
            <td>
              <Text textAlign="center">
                {asset.IncludeInGrossEstate ? (
                  <Icon className="fas fa-check" color="success.500" />
                ) : (
                  <Icon className="fas fa-times-octagon" color="error.500" />
                )}
              </Text>
            </td>
          )}

          <TableActions
            assetName={ASSET_NAME_MAP[OtherAssetType.Pension]}
            onEdit={() => setAssetToUpsert(asset)}
            onDelete={() => setAssetToDelete({ id: asset.Id })}
            onClone={() => onClone(asset)}
          />
        </ClickableTableRow>
      ))}
    </AssetsTable>
  );
};
